import React from 'react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { ScrollArea } from '../../ui/scroll-area';
import UnifiedActivityStream from '../UnifiedActivityStream';
import ActivityLogForm from '../ActivityLogForm';
import { Send, ChevronLeft } from 'lucide-react';

export function ActivityPanel({
  exerciseId, exercise, injects, user,
  loadMessages, loadTimeline, loadResponses,
  // Notes
  noteInjectId, setNoteInjectId,
  noteForm, setNoteForm, handleAddNote,
  // Messages
  messages, messagesLoading,
  msgCompose, setMsgCompose,
  msgSending, handleSendMessage,
  msgViewItem, setMsgViewItem,
  msgReplyText, setMsgReplyText, handleReplyMessage,
  exerciseUsers,
  // V14.5 BUG 5
  onStreamItemClick,
}) {
  // V14.10.5 — Activity stream must render times in the exercise's configured
  // timezone (e.g. "Europe/London" when the controller picked BST), not UTC.
  const exerciseTimezone = exercise?.timezone || 'UTC';
  return (
    <div className="hidden lg:block">
      <Card className="sticky top-4">
        <CardContent className="p-0">
          <Tabs defaultValue="stream" className="w-full" onValueChange={(v) => { if (v === 'messages') loadMessages(); }}>
            <TabsList className="w-full grid grid-cols-4 h-9 rounded-b-none">
              <TabsTrigger value="stream" className="text-xs" data-testid="panel-stream-tab">Stream</TabsTrigger>
              <TabsTrigger value="log-entry" className="text-xs" data-testid="panel-log-tab">Log Entry</TabsTrigger>
              <TabsTrigger value="messages" className="text-xs" data-testid="panel-messages-tab">Messages</TabsTrigger>
              <TabsTrigger value="notes" className="text-xs" data-testid="panel-notes-tab">Notes</TabsTrigger>
            </TabsList>

            <TabsContent value="stream" className="m-0 p-3">
              <ScrollArea className="h-[calc(100vh-300px)]">
                <UnifiedActivityStream exerciseId={exerciseId} timezone={exerciseTimezone} compact onItemClick={onStreamItemClick} />
              </ScrollArea>
            </TabsContent>

            <TabsContent value="log-entry" className="m-0 p-3">
              <ScrollArea className="h-[calc(100vh-300px)]">
                <ActivityLogForm exerciseId={exerciseId} onSaved={loadTimeline} />
              </ScrollArea>
            </TabsContent>

            <TabsContent value="notes" className="m-0 p-3">
              <div className="space-y-3">
                <div className="space-y-2">
                  <Select value={noteInjectId} onValueChange={setNoteInjectId}>
                    <SelectTrigger className="h-8 text-xs" data-testid="note-inject-select">
                      <SelectValue placeholder="Select Inject" />
                    </SelectTrigger>
                    <SelectContent>
                      {injects.filter(i => i.status !== 'draft').map(i => (
                        <SelectItem key={i.inject_id} value={i.inject_id}>
                          {i.title?.substring(0, 40)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={noteForm.type} onValueChange={(v) => setNoteForm({...noteForm, type: v})}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Note Type" /></SelectTrigger>
                    <SelectContent>
                      {['General', 'Action', 'Decision', 'Observation', 'Issue', 'Recommendation'].map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={noteForm.objectiveMet} onValueChange={(v) => setNoteForm({...noteForm, objectiveMet: v})}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Yes">Objective Met</SelectItem>
                      <SelectItem value="No">Not Met</SelectItem>
                      <SelectItem value="Partial">Partial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <textarea
                  className="w-full min-h-[100px] border rounded-md p-2 text-xs bg-background resize-y"
                  placeholder="Add note..."
                  value={noteForm.message}
                  onChange={(e) => setNoteForm({...noteForm, message: e.target.value})}
                  data-testid="note-message-input"
                />
                <div className="flex items-center justify-between">
                  <input
                    type="file"
                    onChange={(e) => setNoteForm({...noteForm, attachment: e.target.files?.[0] || null})}
                    className="text-xs"
                  />
                  <Button size="sm" onClick={handleAddNote} disabled={!noteForm.message.trim() || !noteInjectId} data-testid="add-note-submit">
                    <Send className="w-3 h-3 mr-1" /> Add Note
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* Messages Panel */}
            <TabsContent value="messages" className="m-0 p-3">
              <ScrollArea className="h-[calc(100vh-300px)]">
                {msgViewItem ? (
                  <div className="space-y-3" data-testid="message-detail-view">
                    <button className="text-xs text-primary hover:underline flex items-center gap-1" onClick={() => setMsgViewItem(null)}>
                      <ChevronLeft className="w-3 h-3" /> Back to messages
                    </button>
                    <div className="border rounded-lg p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold">{msgViewItem.subject}</p>
                        <Badge variant="outline" className={`text-[10px] capitalize ${msgViewItem.priority === 'urgent' ? 'text-red-500 border-red-300' : msgViewItem.priority === 'high' ? 'text-orange-500 border-orange-300' : ''}`}>{msgViewItem.priority}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">From: {msgViewItem.sender_name || msgViewItem.sender_id} &middot; {new Date(msgViewItem.created_at).toLocaleString()}</p>
                      <p className="text-sm whitespace-pre-wrap border-t pt-2">{msgViewItem.content}</p>
                    </div>
                    {msgViewItem.replies?.map((r, i) => (
                      <div key={i} className="ml-4 border-l-2 pl-3 py-1">
                        <p className="text-xs font-medium">{r.sender_name || r.sender_id}</p>
                        <p className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</p>
                        <p className="text-sm mt-1">{r.content}</p>
                      </div>
                    ))}
                    <div className="flex gap-2 mt-2">
                      <Input
                        value={msgReplyText}
                        onChange={(e) => setMsgReplyText(e.target.value)}
                        placeholder="Type a reply..."
                        className="h-8 text-xs flex-1"
                        onKeyDown={(e) => { if (e.key === 'Enter') handleReplyMessage(msgViewItem.message_id); }}
                        data-testid="message-reply-input"
                      />
                      <Button size="sm" className="h-8" onClick={() => handleReplyMessage(msgViewItem.message_id)} disabled={!msgReplyText.trim()} data-testid="message-reply-btn">
                        <Send className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="border rounded-lg p-3 space-y-2" data-testid="message-compose">
                      <p className="text-xs font-semibold">New Message</p>
                      <Select value={msgCompose.recipient_type} onValueChange={(v) => setMsgCompose({...msgCompose, recipient_type: v, recipient_id: '', recipient_role: ''})}>
                        <SelectTrigger className="h-7 text-xs" data-testid="msg-send-to"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="broadcast">Everyone in Exercise</SelectItem>
                          <SelectItem value="role">Role Group</SelectItem>
                          <SelectItem value="individual">Specific Person</SelectItem>
                        </SelectContent>
                      </Select>
                      {msgCompose.recipient_type === 'role' && (
                        <Select value={msgCompose.recipient_role} onValueChange={(v) => setMsgCompose({...msgCompose, recipient_role: v})}>
                          <SelectTrigger className="h-7 text-xs" data-testid="msg-role-select"><SelectValue placeholder="Select role..." /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="super_admin">Admins</SelectItem>
                            <SelectItem value="simcel">Simcells</SelectItem>
                            <SelectItem value="participant">Participants</SelectItem>
                            <SelectItem value="observer">Observers</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                      {msgCompose.recipient_type === 'individual' && (
                        <Select value={msgCompose.recipient_id} onValueChange={(v) => setMsgCompose({...msgCompose, recipient_id: v})}>
                          <SelectTrigger className="h-7 text-xs" data-testid="msg-person-select"><SelectValue placeholder="Select person..." /></SelectTrigger>
                          <SelectContent>
                            {exerciseUsers.filter(u => u.user_id !== user?.user_id).map(u => (
                              <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.roles?.join(', ')})</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      <div className="grid grid-cols-2 gap-2">
                        <Input value={msgCompose.subject} onChange={(e) => setMsgCompose({...msgCompose, subject: e.target.value})} placeholder="Subject" className="h-7 text-xs" data-testid="message-subject-input" />
                        <Select value={msgCompose.priority} onValueChange={(v) => setMsgCompose({...msgCompose, priority: v})}>
                          <SelectTrigger className="h-7 text-xs" data-testid="message-priority-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="normal">Normal</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="urgent">Urgent</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <textarea
                        className="w-full min-h-[60px] border rounded-md p-2 text-xs bg-background resize-y"
                        placeholder="Message content..."
                        value={msgCompose.content}
                        onChange={(e) => setMsgCompose({...msgCompose, content: e.target.value})}
                        data-testid="message-content-input"
                      />
                      <div className="flex justify-end">
                        <Button size="sm" className="h-7 text-xs" onClick={handleSendMessage} disabled={msgSending || !msgCompose.subject.trim() || !msgCompose.content.trim()} data-testid="message-send-btn">
                          <Send className="w-3 h-3 mr-1" />{msgSending ? 'Sending...' : 'Send'}
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      {messagesLoading ? (
                        <p className="text-xs text-muted-foreground text-center py-4">Loading messages...</p>
                      ) : messages.length === 0 ? (
                        <p className="text-xs text-muted-foreground text-center py-4">No messages yet</p>
                      ) : (
                        messages.map((m) => (
                          <button
                            key={m.message_id}
                            className="w-full text-left p-2.5 rounded-lg border hover:bg-muted/50 transition-colors space-y-0.5"
                            onClick={() => setMsgViewItem(m)}
                            data-testid={`message-item-${m.message_id}`}
                          >
                            <div className="flex items-center justify-between">
                              <p className="text-xs font-semibold truncate flex-1">{m.subject}</p>
                              <Badge variant="outline" className={`text-[9px] ml-2 flex-shrink-0 ${m.priority === 'urgent' ? 'text-red-500 border-red-300' : m.priority === 'high' ? 'text-orange-500 border-orange-300' : ''}`}>{m.priority}</Badge>
                            </div>
                            <p className="text-[10px] text-muted-foreground truncate">{m.content}</p>
                            <div className="flex items-center justify-between">
                              <p className="text-[10px] text-muted-foreground">{m.sender_name || m.sender_id}</p>
                              <p className="text-[10px] text-muted-foreground">{new Date(m.created_at).toLocaleTimeString()}</p>
                            </div>
                            {m.replies?.length > 0 && (
                              <p className="text-[10px] text-primary">{m.replies.length} {m.replies.length === 1 ? 'reply' : 'replies'}</p>
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
